/**
 * ReportShipmentDrawer — KAN-12 "Reportar pedido"
 *
 * Marca 1 sale_item como frustrado com um tipo (frustration_type) + motivo.
 * O card sai da aba Geral e cai na coluna correspondente da aba Frustrações.
 *
 * Backend: POST /api/v2/sales/items/:saleItemId/report
 *          GET  /api/v2/sales/items/frustration-types
 * Spec: docs/V2/07_REGRAS_NEGOCIO.md §5.x (KAN-12)
 */

import React, { useEffect, useState } from 'react';
import { Icon } from '@iconify/react';
import { toast } from 'react-toastify';
import { salesV2API } from '../../services/api/v2/sales';
import { parseApiError, scrollToFirstError } from '../../utils/apiErrors';
import Drawer from '../products/details/Drawer';

const ReportShipmentDrawer = ({ isOpen, onClose, card, onReported }) => {
  const [frustrationType, setFrustrationType] = useState('');
  const [reason, setReason] = useState('');
  const [types, setTypes] = useState([]);
  const [loadingTypes, setLoadingTypes] = useState(false);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [topError, setTopError] = useState('');

  // Reset estado e carrega tipos quando abrir
  useEffect(() => {
    if (!isOpen) return;
    setFrustrationType('');
    setReason('');
    setErrors({});
    setTopError('');

    let cancelled = false;
    const loadTypes = async () => {
      try {
        setLoadingTypes(true);
        const res = await salesV2API.getFrustrationTypes();
        const fetched = res.data?.types || [];
        if (!cancelled) setTypes(fetched);
      } catch (err) {
        if (!cancelled) {
          const { message } = parseApiError(err, 'Erro ao carregar tipos de frustração');
          setTopError(message);
        }
      } finally {
        if (!cancelled) setLoadingTypes(false);
      }
    };
    loadTypes();
    return () => {
      cancelled = true;
    };
  }, [isOpen, card?.saleItemId]);

  const validate = () => {
    const err = {};
    if (!frustrationType) err.frustration_type = 'Selecione pra onde mover o pedido';
    const trimmed = reason.trim();
    if (!trimmed) err.reason = 'Informe o motivo';
    else if (trimmed.length < 3) err.reason = 'Motivo deve ter no mínimo 3 caracteres';
    else if (trimmed.length > 500) err.reason = 'Máximo 500 caracteres';
    setErrors(err);
    return Object.keys(err).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate() || submitting) return;
    if (!card?.saleItemId) {
      setTopError('Item da venda não identificado.');
      return;
    }
    try {
      setSubmitting(true);
      setTopError('');
      await salesV2API.report(card.saleItemId, {
        frustration_type: frustrationType,
        reason: reason.trim(),
      });
      toast.success('Pedido reportado');
      onReported?.(card);
      onClose?.();
    } catch (err) {
      const { fieldErrors, message } = parseApiError(err, 'Erro ao reportar pedido');
      if (Object.keys(fieldErrors).length) {
        setErrors((p) => ({ ...p, ...fieldErrors }));
        scrollToFirstError(fieldErrors);
      }
      setTopError(message);
    } finally {
      setSubmitting(false);
    }
  };

  const isShared = card?.kan17?.isShared;

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      title="Reportar pedido"
      subtitle={card?.kan17?.badge || (card?.saleId ? `Venda #${card.saleId}` : '')}
      icon="mdi:alert-octagon-outline"
      onSubmit={handleSubmit}
      submitting={submitting}
      submitLabel="Reportar pedido"
    >
      {topError && (
        <div className="drawer-alert drawer-alert--error">
          <Icon icon="mdi:alert-circle" width="16" />
          <div>{topError}</div>
        </div>
      )}

      <div className="drawer-alert drawer-alert--warning">
        <Icon icon="mdi:alert-outline" width="18" />
        <div>
          <strong>O card sai da aba Geral.</strong> Ao confirmar:
          <ul style={{ margin: '8px 0 0', paddingLeft: 20, fontSize: 13 }}>
            <li>O item é movido pra coluna escolhida na aba <b>Frustrações</b></li>
            <li>O motivo fica registrado no histórico junto com seu nome</li>
            <li>Você pode reverter movendo o card de volta no Kanban de Frustrações</li>
            {isShared && (
              <li>
                Os <b>demais {card.kan17.total - 1} itens</b> da venda <b>#{card.saleId}</b> seguem normalmente
              </li>
            )}
          </ul>
        </div>
      </div>

      <div className="drawer-field">
        <span className="drawer-label">Item</span>
        <div className="drawer-input" style={{ background: '#f9fafb', cursor: 'default' }}>
          {card?.description || `Item #${card?.saleItemId}`}
        </div>
      </div>

      <div className="drawer-field">
        <span className="drawer-label">Cliente</span>
        <div className="drawer-input" style={{ background: '#f9fafb', cursor: 'default' }}>
          {card?.name || '—'}
          {card?.email ? ` · ${card.email}` : ''}
        </div>
      </div>

      <div className="drawer-field">
        <span className="drawer-label drawer-label--required">Mover pra coluna</span>
        <select
          className="drawer-select"
          value={frustrationType}
          onChange={(e) => {
            setFrustrationType(e.target.value);
            setErrors((p) => ({ ...p, frustration_type: undefined }));
          }}
          disabled={loadingTypes || submitting}
          data-field="frustration_type"
        >
          <option value="">
            {loadingTypes ? 'Carregando…' : 'Selecione a coluna…'}
          </option>
          {types.map((t) => (
            <option key={t.value} value={t.value}>{t.label}</option>
          ))}
        </select>
        {errors.frustration_type && (
          <span className="drawer-error">
            <Icon icon="mdi:alert-circle-outline" width="12" /> {errors.frustration_type}
          </span>
        )}
      </div>

      <div className="drawer-field">
        <span className="drawer-label drawer-label--required">Motivo</span>
        <textarea
          className="drawer-input"
          rows={4}
          value={reason}
          onChange={(e) => {
            setReason(e.target.value);
            setErrors((p) => ({ ...p, reason: undefined }));
          }}
          placeholder="Ex: Cliente alegou roubo da encomenda, produto retornado pelos Correios sem motivo claro, solicitação de cancelamento pós-envio…"
          maxLength={500}
          data-field="reason"
        />
        <span className="drawer-help">
          {reason.length}/500 caracteres. Registrado no histórico junto com seu nome.
        </span>
        {errors.reason && (
          <span className="drawer-error">
            <Icon icon="mdi:alert-circle-outline" width="12" /> {errors.reason}
          </span>
        )}
      </div>
    </Drawer>
  );
};

export default ReportShipmentDrawer;
